import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { useTicketsStore } from '../store/ticketsStore';
import {
  IS_MERCH_COUNT_IN_TOT,
  MAX_CART_ITEM_AMOUNT,
  getTotalAmount,
  getTotalProductNum,
} from './utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
});

const Item = ({ theme, product, locale, i18n }) => {
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <>
      <Typography
        variant={`${isMdDown ? 'm' : 'body2'}-bold`}
        fontFamily={'Suisse-Intl'}
        component={'p'}
      >
        {!product.capacity
          ? `${i18n.cartMerch} ${new Date().getFullYear()}`
          : i18n.cartTicket}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: '15px' }}
        fontFamily={'Suisse-Intl'}
        component={'p'}
      >
        {product?.description_map?.[locale] ||
          product?.description_map?.it ||
          product?.description ||
          ''}
      </Typography>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        sx={{ mb: '15px' }}
      >
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          gap={'5px'}
          alignItems={'center'}
        >
          {!product.capacity ? (
            <MenuBookOutlinedIcon
              sx={{
                color: theme.palette.grey[800],
                mb: '3px',
              }}
              fontSize="20px"
            />
          ) : (
            <ConfirmationNumberOutlinedIcon
              sx={{
                color: theme.palette.grey[800],
                mb: '3px',
                height: '24px !important',
              }}
              fontSize="20px"
            />
          )}
          <Typography
            variant="body2"
            color="grey.800"
            fontFamily={'Suisse-Intl'}
            component={'p'}
          >
            {product?.quantity}
          </Typography>
        </Stack>
        <Typography
          variant="body2-bold"
          fontFamily={'Suisse-Intl'}
          component={'p'}
          color={'#333333'}
        >
          {product?.current_price
            ? currencyFormatter.format(
                product?.current_price * product.quantity
              )
            : null}
        </Typography>
      </Stack>
      <Divider sx={{ mb: '15px' }} />
    </>
  );
};

const TicketsCart = ({ locale, procideFn, btnDisabled, procideMsg = '' }) => {
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { productsInCart, merchInCart } = useTicketsStore();
  const intl = useIntl();
  const i18n = translations.TicketsV2(intl).Buy;
  const i18nCommon = translations.TicketsV2(intl).Common;

  const toggleDetails = () => setIsDetailOpen((prev) => !prev);

  useEffect(() => {
    const totalInCart = IS_MERCH_COUNT_IN_TOT
      ? getTotalProductNum(productsInCart.concat(merchInCart))
      : getTotalProductNum(productsInCart);
    useTicketsStore.setState({
      isCartFull: totalInCart >= MAX_CART_ITEM_AMOUNT,
    });
  }, [productsInCart, merchInCart]);

  return productsInCart?.length || merchInCart?.length ? (
    <>
      {!isLgDown ? (
        <Box
          sx={{
            width: isLgDown ? '100%' : '36%',
            mt: '25px',
          }}
        >
          <Stack
            direction={'row'}
            justifyContent={'flex-end'}
            sx={{ position: 'sticky', top: '96px' }}
          >
            <Stack
              sx={{
                p: '15px 25px',
                boxShadow: '0px 0px 5px 0px rgba(51, 51, 51, 0.05)',
                bgcolor: 'white',
                minWidth: '359px',
              }}
              className="react-floating-cart"
            >
              {productsInCart
                .filter((pro) => pro && pro.quantity && pro.quantity > 0)
                .map((pro) => (
                  <Item
                    key={pro.id}
                    theme={theme}
                    product={pro}
                    locale={locale}
                    i18n={i18n}
                  />
                ))}
              {merchInCart
                .filter(
                  (merch) => merch && merch.quantity && merch.quantity > 0
                )
                .map((merch) => (
                  <Item
                    key={merch.id}
                    theme={theme}
                    product={merch}
                    locale={locale}
                    i18n={i18n}
                  />
                ))}
              <Stack
                justifyContent={'space-between'}
                alignItems={'center'}
                direction={'row'}
                sx={{ p: '15px 0' }}
              >
                <Typography
                  variant="body2-bold"
                  color="primary"
                  fontFamily={'Suisse-Intl'}
                  component={'p'}
                >
                  {getTotalProductNum(productsInCart.concat(merchInCart))}{' '}
                  {
                    i18nCommon[
                      getTotalProductNum(productsInCart.concat(merchInCart)) ===
                      1
                        ? 'article'
                        : 'articles'
                    ]
                  }
                </Typography>
                <Typography
                  variant="body2-bold"
                  color="black"
                  fontFamily={'Suisse-Intl'}
                  component={'p'}
                >
                  {getTotalAmount(productsInCart.concat(merchInCart))}
                </Typography>
              </Stack>
              <Button
                variant="contained"
                sx={{ width: '100%', fontSize: '20px', lineHeight: '28px' }}
                onClick={() => {
                  window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth',
                  });
                  procideFn();
                }}
                disabled={btnDisabled}
              >
                {procideMsg || i18nCommon.proceedMsg}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            mt: '25px',
            position: 'fixed',
            bottom: 0,
            left: 0,
            backgroundColor: 'white',
            zIndex: 4,
            p: '25px',
            boxShadow: '0px 0px 15px 0px rgba(51, 51, 51, 0.15)',
          }}
        >
          <Stack
            justifyContent={'space-between'}
            alignItems={'center'}
            direction={'row'}
            sx={{ p: '15px 0' }}
          >
            <Stack
              alignItems={'center'}
              direction={'row'}
              sx={{
                ...(isLgDown && {
                  cursor: 'pointer',
                }),
              }}
              onClick={toggleDetails}
            >
              <Typography
                variant="body2-bold"
                color="primary"
                fontFamily={'Suisse-Intl'}
                component={'p'}
              >
                {getTotalProductNum(productsInCart.concat(merchInCart))}{' '}
                {
                  i18nCommon[
                    getTotalProductNum(productsInCart.concat(merchInCart)) === 1
                      ? 'article'
                      : 'articles'
                  ]
                }
              </Typography>
              {isLgDown ? (
                <>
                  {isDetailOpen ? (
                    <ExpandLessIcon color="primary" />
                  ) : (
                    <ExpandMoreIcon color="primary" />
                  )}
                </>
              ) : null}
            </Stack>
            <Typography
              variant="body2-bold"
              color="black"
              fontFamily={'Suisse-Intl'}
              component={'p'}
            >
              {getTotalAmount(productsInCart.concat(merchInCart))}
            </Typography>
          </Stack>
          {isLgDown && isDetailOpen ? (
            <div>
              {productsInCart
                .filter((pro) => pro && pro.quantity && pro.quantity > 0)
                .map((pro) => (
                  <Item
                    key={pro.id}
                    theme={theme}
                    product={pro}
                    locale={locale}
                    i18n={i18n}
                  />
                ))}
              {merchInCart
                .filter(
                  (merch) => merch && merch.quantity && merch.quantity > 0
                )
                .map((merch) => (
                  <Item
                    key={merch.id}
                    theme={theme}
                    product={merch}
                    locale={locale}
                    i18n={i18n}
                  />
                ))}
            </div>
          ) : null}
          <Button
            variant="contained"
            sx={{ width: '100%', zIndex: 100 }}
            onClick={() => procideFn()}
            disabled={btnDisabled}
          >
            {procideMsg || i18nCommon.proceedMsg}
          </Button>
        </Box>
      )}
    </>
  ) : null;
};
export { TicketsCart };
